import React, { useState, useEffect, useContext, useRef } from 'react';
import LiffService from '../../../services/LiffService';
import LiffContext from '../context/LiffContext';
import { fmtBaht } from '../utils/formatters';

const AwaitingActionPage = ({ skipAutoRedirectToPayment, onViewOrderSummary, onViewSelectAction }) => {
  const liff = useContext(LiffContext);
  const { accessToken, shopId } = liff || {};
  const [awaitingData, setAwaitingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // When customer closed after paying group1 only: we show "ชำระยอดที่ 2" CTA
  const [paymentSummary, setPaymentSummary] = useState(null);
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!accessToken || !shopId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    LiffService.getAwaitingAction(accessToken, shopId)
      .then((data) => {
        if (!cancelled) setAwaitingData(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.response?.data?.message || err.message || 'โหลดข้อมูลไม่สำเร็จ');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [accessToken, shopId]);

  // Fetch payment summary whenever we have awaiting data: for routing (resume flow) and for "ชำระยอดที่ 2" banner
  useEffect(() => {
    if (!accessToken || !shopId || !awaitingData) {
      setPaymentSummary(null);
      hasRedirectedRef.current = false;
      return;
    }
    hasRedirectedRef.current = false;
    let cancelled = false;
    LiffService.getPaymentSummary(accessToken, shopId)
      .then((data) => {
        if (cancelled) return;
        setPaymentSummary(data);
        if (hasRedirectedRef.current) return;
        if (skipAutoRedirectToPayment) return; // User pressed Back from payment; show dashboard and let them click "ชำระเงิน"
        const g1 = data?.group1Total ?? 0;
        const g2 = data?.group2Total ?? 0;
        const needsDelivery = !!data?.needsDeliverySelection;
        const itemsList = awaitingData?.items ?? [];
        const paymentStatusFromData = awaitingData?.paymentStatus;
        if ((g1 > 0 || g2 > 0) && typeof onViewSelectAction === 'function') {
          hasRedirectedRef.current = true;
          onViewSelectAction(itemsList, { ...paymentStatusFromData, requiresPayment: true });
          return;
        }
        if (needsDelivery && itemsList.length > 0 && typeof onViewSelectAction === 'function') {
          hasRedirectedRef.current = true;
          onViewSelectAction(itemsList, paymentStatusFromData);
        }
      })
      .catch(() => {
        if (!cancelled) setPaymentSummary(null);
      });
    return () => { cancelled = true; };
  }, [accessToken, shopId, awaitingData, onViewSelectAction, skipAutoRedirectToPayment]);

  const items = awaitingData?.items ?? [];
  const totalQty = awaitingData?.totalQty ?? 0;
  const totalWeight = awaitingData?.totalWeight ?? 0;
  const amountInList = awaitingData?.amountInList ?? 0;
  const amountOtherThanList = awaitingData?.amountOtherThanList ?? 0;
  const totalToPay = awaitingData?.totalToPay ?? 0;

  // Customer closed after paying group1 only — show "ชำระยอดที่ 2" so they can continue
  const group2OnlyPending =
    awaitingData?.paymentStatus?.requiresPayment &&
    paymentSummary != null &&
    (paymentSummary.group1Total ?? 0) <= 0 &&
    (paymentSummary.group2Total ?? 0) > 0;
  const group2Total = paymentSummary?.group2Total ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-8">
      <div className="max-w-sm mx-auto pt-6">
        {/* Banner: customer closed after paying group1 — remind to complete group2 */}
        {!loading && !loadError && group2OnlyPending && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-medium text-amber-800 mb-1">คุณชำระยอดที่ 1 แล้ว</p>
            <p className="text-sm text-amber-700">
              ยังมียอดที่ 2 (ค่าน้ำหนัก/ค่าส่งไทย) ค้างชำระ {Number.isFinite(group2Total) ? `${group2Total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท` : ''}
            </p>
            <p className="text-xs text-amber-600 mt-2">กดปุ่มด้านล่างเพื่อชำระต่อ</p>
          </div>
        )}

        {/* Summary card: in this list vs other than this list */}
        {!loading && !loadError && items.length > 0 && (
          <div className="mb-4 p-4 bg-white rounded-2xl shadow-lg space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">สรุป</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">ยอดค่าใช้จ่ายในรายการนี้</span>
              <span className="font-semibold text-gray-800">{fmtBaht(amountInList)} บาท</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">จำนวน</span>
              <span className="font-semibold text-gray-800">{totalQty} ชิ้น</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">น้ำหนักรวม</span>
              <span className="font-semibold text-gray-800">{Number(totalWeight).toFixed(2)} kg</span>
            </div>
            {amountOtherThanList > 0 && (
              <>
                <div className="border-t border-gray-100 pt-2 flex justify-between text-sm">
                  <span className="text-gray-600">ยอดที่ต้องชำระนอกรายการนี้</span>
                  <span className="font-semibold text-amber-600">{fmtBaht(amountOtherThanList)} บาท</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-sm font-semibold text-gray-800">
                  <span>รวมที่ต้องชำระ</span>
                  <span>{fmtBaht(totalToPay)} บาท</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Awaiting action list */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4">
          <h2 className="text-base font-semibold text-gray-800 px-4 py-3 border-b border-gray-100">
            รายการรอดำเนินการ
          </h2>

          {loading && (
            <div className="p-8 text-center text-gray-500 text-sm">กำลังโหลด...</div>
          )}
          {loadError && (
            <div className="p-4 text-center text-amber-600 text-sm">{loadError}</div>
          )}
          {!loading && !loadError && items.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">ไม่มีรายการรอดำเนินการ</div>
          )}

          {!loading && !loadError && items.length > 0 && (
            <>
              <ul className="divide-y divide-gray-100">
                {items.map((item) => (
                  <li key={item.id} className="p-4 flex gap-3">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {item.shipDate && (
                        <p className="text-gray-600 text-sm">รอบเรือ: {item.shipDate}</p>
                      )}
                      {item.amount !== '' && (
                        <p className="text-gray-800 font-medium text-sm">ยอดรอชำระ: {item.amount} บาท</p>
                      )}
                      {item.weight !== '' && (
                        <p className="text-gray-500 text-sm">น้ำหนัก: {item.weight} kg</p>
                      )}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-1 text-green-600 text-sm underline truncate max-w-full"
                        >
                          เปิดลิงก์
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Link to order summary: show all charged items (in-list + other), same order as first page */}
              {typeof onViewOrderSummary === 'function' && (
                <div className="px-4 py-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() =>
                      onViewOrderSummary(
                        awaitingData?.orderIdsForSummary ?? items.map((i) => i.id),
                        awaitingData?.orderIdsByGroup ?? null
                      )
                    }
                    className="w-full text-center text-green-600 text-sm font-medium underline hover:text-green-700"
                  >
                    ต้องการดูรายละเอียดแต่ละรายการ คลิกที่นี่
                  </button>
                </div>
              )}

              {/* Single button: ชำระเงิน / ชำระยอดที่ 2 (if closed after first payment) / เลือกวิธีรับสินค้า */}
              {typeof onViewSelectAction === 'function' && (
                <div className="px-4 py-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() =>
                      onViewSelectAction(items, awaitingData?.paymentStatus, {
                        group2Only: group2OnlyPending,
                      })
                    }
                    className={`w-full py-3 rounded-xl font-semibold text-base ${
                      awaitingData?.paymentStatus?.requiresPayment
                        ? 'bg-amber-400 hover:bg-amber-500 text-gray-900'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {awaitingData?.paymentStatus?.requiresPayment
                      ? group2OnlyPending
                        ? 'ชำระยอดที่ 2 (ค่าน้ำหนัก/ค่าส่งไทย)'
                        : 'ชำระเงิน'
                      : 'เลือกวิธีรับสินค้า'}
                  </button>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AwaitingActionPage;
